// File: /var/www/app1/frontend/src/components/MissionaryForm.js

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import heic2any from "heic2any";

function MissionaryForm({ selectedMissionary, onSuccess, onSaveAndClose, initialDraft }) {
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  const [formData, setFormData] = useState({
    month: currentMonth.toString(),
    year: currentYear.toString(),
    workerNumber: selectedMissionary?.missionary_number || "",
    heardGospel: "",
    receivedJesus: "",
    baptized: "",
    discoveryBibleStudyStarted: "",
    churchesPlanted: "",
    leadersTrained: "",
    testimony: "",
    challenges: "",
    prayerRequests: "",
  });

  // New state for picture management
  const [picturesList, setPicturesList] = useState([]);
  const [currentPicture, setCurrentPicture] = useState(null);
  const [currentDescription, setCurrentDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [processingImage, setProcessingImage] = useState(false);
  const [originalReport, setOriginalReport] = useState(null);
  // Original report already stored with the saved draft: { filename, name }
  const [savedOriginalReport, setSavedOriginalReport] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const draftLoaded = useRef(false);

  // Load draft data when initialDraft is provided
  useEffect(() => {
    if (initialDraft && !draftLoaded.current) {
      draftLoaded.current = true;
      const draft = initialDraft;
      const fd = draft.form_data || {};

      setFormData({
        month: String(draft.month || currentMonth),
        year: String(draft.year || currentYear),
        workerNumber: selectedMissionary?.missionary_number || "",
        heardGospel: fd.heardGospel != null ? String(fd.heardGospel) : "",
        receivedJesus: fd.receivedJesus != null ? String(fd.receivedJesus) : "",
        baptized: fd.baptized != null ? String(fd.baptized) : "",
        discoveryBibleStudyStarted: fd.discoveryBibleStudyStarted != null ? String(fd.discoveryBibleStudyStarted) : "",
        churchesPlanted: fd.churchesPlanted != null ? String(fd.churchesPlanted) : "",
        leadersTrained: fd.leadersTrained != null ? String(fd.leadersTrained) : "",
        testimony: fd.testimony || "",
        challenges: fd.challenges || "",
        prayerRequests: fd.prayerRequests || "",
      });

      // Load draft images as server-image entries
      const filenames = draft.image_filenames || [];
      const descriptions = draft.image_descriptions || [];
      const draftImages = filenames.map((filename, idx) => ({
        isServerImage: true,
        serverFilename: filename,
        description: descriptions[idx] || "",
        preview: `/api/drafts/${draft.missionary_id}/images/${filename}`,
        originalName: filename,
      }));
      setPicturesList(draftImages);

      if (draft.original_report_filename) {
        setSavedOriginalReport({
          filename: draft.original_report_filename,
          name: draft.original_report_name || draft.original_report_filename,
        });
      }
    }
  }, [initialDraft, selectedMissionary, currentMonth, currentYear]);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      // Clean up all preview URLs when component unmounts
      picturesList.forEach(item => {
        if (item.preview && !item.isServerImage) {
          URL.revokeObjectURL(item.preview);
        }
      });
    };
  }, [picturesList]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Helper function to check if file is HEIC/HEIF
  const isHeicFile = (file) => {
    const heicTypes = ["image/heic", "image/heif"];
    const fileName = file.name.toLowerCase();
    return heicTypes.includes(file.type) || /\.(heic|heif)$/i.test(fileName);
  };

  // Helper function to convert HEIC to JPEG for preview and compatibility
  const convertHeicToJpeg = async (file) => {
    try {
      const convertedBlob = await heic2any({
        blob: file,
        toType: "image/jpeg",
        quality: 0.8, // Good balance between quality and file size
      });

      // Create a new File object from the converted blob
      const convertedFile = new File(
        [convertedBlob],
        file.name.replace(/\.(heic|heif)$/i, ".jpg"),
        {
          type: "image/jpeg",
          lastModified: file.lastModified,
        }
      );

      return convertedFile;
    } catch (error) {
      console.error("HEIC conversion failed:", error);
      throw new Error("Failed to convert HEIC image. Please try a different image or convert to JPEG first.");
    }
  };

  // Handle single picture selection
  const handlePictureSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
        "image/heic",
        "image/heif",
      ];

      // Check file extension as backup (HEIC files sometimes have undefined MIME type)
      const fileName = file.name.toLowerCase();
      const hasValidExtension = /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(fileName);

      if (!allowedTypes.includes(file.type) && !hasValidExtension) {
        setError("Please select a valid image file (JPEG, PNG, GIF, WebP, or HEIC)");
        e.target.value = "";
        return;
      }

      // Validate file size (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        setError("Image size must be less than 50MB");
        e.target.value = "";
        return;
      }

      // Handle HEIC conversion for preview
      try {
        setProcessingImage(true);
        setError("");

        if (isHeicFile(file)) {
          // Convert HEIC to JPEG for better browser compatibility
          const convertedFile = await convertHeicToJpeg(file);
          setCurrentPicture({
            originalFile: file, // Keep original for upload
            displayFile: convertedFile, // Use converted for preview
            isConverted: true
          });
          setSuccess("HEIC image converted successfully for preview");
          setTimeout(() => setSuccess(""), 3000);
        } else {
          setCurrentPicture({
            originalFile: file,
            displayFile: file,
            isConverted: false
          });
        }
      } catch (error) {
        setError(error.message);
        e.target.value = "";
      } finally {
        setProcessingImage(false);
      }
    }
  };

  // Add picture to the list
  const handleAddPicture = () => {
    if (!currentPicture) {
      setError("Please select a picture first");
      return;
    }

    if (!currentDescription.trim()) {
      setError("Please add a description for the picture");
      return;
    }

    if (picturesList.length >= 10) {
      setError("Maximum 10 pictures allowed");
      return;
    }

    // Add to pictures list
    setPicturesList([
      ...picturesList,
      {
        file: currentPicture.originalFile, // Use original file for upload
        displayFile: currentPicture.displayFile, // Use display file for preview
        description: currentDescription.trim(),
        preview: URL.createObjectURL(currentPicture.displayFile),
        isConverted: currentPicture.isConverted,
        originalName: currentPicture.originalFile.name
      },
    ]);

    // Reset current picture fields
    setCurrentPicture(null);
    setCurrentDescription("");
    document.getElementById("picture-input").value = "";
    setError("");
    setSuccess(`Picture added successfully! (${picturesList.length + 1}/10)`);

    // Clear success message after 3 seconds
    setTimeout(() => setSuccess(""), 3000);
  };

  // Remove picture from list
  const handleRemovePicture = (index) => {
    const itemToRemove = picturesList[index];

    // Clean up the preview URL to prevent memory leaks (only for local files)
    if (itemToRemove.preview && !itemToRemove.isServerImage) {
      URL.revokeObjectURL(itemToRemove.preview);
    }

    const newList = picturesList.filter((_, i) => i !== index);
    setPicturesList(newList);
    setSuccess(`Picture removed. (${newList.length} pictures added)`);
    setTimeout(() => setSuccess(""), 3000);
  };

  // Move picture up in the list
  const handleMoveUp = (index) => {
    if (index === 0) return; // Already at top
    const newList = [...picturesList];
    [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
    setPicturesList(newList);
  };

  // Move picture down in the list
  const handleMoveDown = (index) => {
    if (index === picturesList.length - 1) return; // Already at bottom
    const newList = [...picturesList];
    [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
    setPicturesList(newList);
  };

  // Save draft
  const handleSaveDraft = async (closeAfterSave) => {
    setError("");
    setSuccess("");
    setSaving(true);

    try {
      const saveData = new window.FormData();
      saveData.append('missionaryNumber', formData.workerNumber);
      saveData.append('month', formData.month);
      saveData.append('year', formData.year);

      // Form data as JSON
      const reportData = {
        heardGospel: formData.heardGospel ? parseInt(formData.heardGospel) : null,
        receivedJesus: formData.receivedJesus ? parseInt(formData.receivedJesus) : null,
        baptized: formData.baptized ? parseInt(formData.baptized) : null,
        discoveryBibleStudyStarted: formData.discoveryBibleStudyStarted ? parseInt(formData.discoveryBibleStudyStarted) : null,
        churchesPlanted: formData.churchesPlanted ? parseInt(formData.churchesPlanted) : null,
        leadersTrained: formData.leadersTrained ? parseInt(formData.leadersTrained) : null,
        testimony: formData.testimony || "",
        challenges: formData.challenges || "",
        prayerRequests: formData.prayerRequests || "",
      };
      saveData.append('data', JSON.stringify(reportData));

      // Separate server images (existing) from new local files
      const serverImages = picturesList.filter(p => p.isServerImage);
      const localImages = picturesList.filter(p => !p.isServerImage);

      // Existing server image filenames to keep
      saveData.append('existingImages', JSON.stringify(serverImages.map(p => p.serverFilename)));

      // New image descriptions
      saveData.append('imageDescriptions', JSON.stringify(localImages.map(p => p.description || "")));

      // New image files
      localImages.forEach(imageItem => {
        saveData.append('images', imageItem.file, imageItem.file.name);
      });

      // Original Missionary Report: a newly chosen file replaces the saved one
      if (originalReport) {
        saveData.append('originalReport', originalReport, originalReport.name);
      } else if (savedOriginalReport) {
        saveData.append('keepOriginalReport', 'true');
      }

      const response = await axios.post('/api/drafts/save', saveData);

      if (response.data.success) {
        if (response.data.originalReport) {
          setSavedOriginalReport(response.data.originalReport);
          setOriginalReport(null);
          const input = document.getElementById('original-report-input');
          if (input) input.value = "";
        }
        setSuccess("Draft saved successfully!");
        setTimeout(() => setSuccess(""), 3000);

        if (closeAfterSave && onSaveAndClose) {
          onSaveAndClose();
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save draft");
    } finally {
      setSaving(false);
    }
  };

  const ORIGINAL_REPORT_MAX_MB = 25;
  const ORIGINAL_REPORT_EXT = /\.(pdf|docx?|jpe?g)$/i;

  const handleOriginalReportSelect = (e) => {
    setError("");
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setOriginalReport(null);
      return;
    }
    if (!ORIGINAL_REPORT_EXT.test(file.name)) {
      setError("The Original Missionary Report must be a PDF, Word (.doc/.docx) or JPG file.");
      e.target.value = "";
      setOriginalReport(null);
      return;
    }
    if (file.size > ORIGINAL_REPORT_MAX_MB * 1024 * 1024) {
      setError(`That file is ${Math.round(file.size / 1024 / 1024)} MB. The Original Missionary Report must be under ${ORIGINAL_REPORT_MAX_MB} MB.`);
      e.target.value = "";
      setOriginalReport(null);
      return;
    }
    setOriginalReport(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validate pictures
    if (picturesList.length < 2) {
      setError("Please upload at least 2 pictures");
      return;
    }

    if (!originalReport && !savedOriginalReport) {
      setError("Please attach the Original Missionary Report (PDF, Word or JPG)");
      return;
    }

    // Show confirmation modal instead of submitting immediately
    setShowConfirmation(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmation(false);
    setLoading(true);

    // Pre-flight token check: refresh token on a lightweight request
    // before attempting the large multipart upload
    try {
      await axios.get('/api/auth/me');
    } catch (preflight) {
      if (!localStorage.getItem('accessToken')) {
        setError('Your session has expired. Please log in again.');
        setLoading(false);
        return;
      }
    }

    // Create FormData for multipart upload
    const submitData = new FormData();

    // Basic form fields for the new App2 format
    submitData.append('missionaryNumber', formData.workerNumber);
    submitData.append('year', formData.year);
    submitData.append('month', formData.month);

    // Report data as JSON string (WITHOUT pictures array)
    const reportData = {
      heardGospel: parseInt(formData.heardGospel) || 0,
      receivedJesus: parseInt(formData.receivedJesus) || 0,
      baptized: parseInt(formData.baptized) || 0,
      discoveryBibleStudyStarted: parseInt(formData.discoveryBibleStudyStarted) || 0,
      churchesPlanted: parseInt(formData.churchesPlanted) || 0,
      leadersTrained: parseInt(formData.leadersTrained) || 0,
      testimony: formData.testimony || "",
      challenges: formData.challenges || "",
      prayerRequests: formData.prayerRequests || ""
    };
    submitData.append('data', JSON.stringify(reportData));

    // Separate server images (from draft) and local files
    const serverImages = picturesList.filter(p => p.isServerImage);
    const localImages = picturesList.filter(p => !p.isServerImage);

    // Draft image filenames for the backend to read from disk
    if (serverImages.length > 0) {
      submitData.append('draftImageFilenames', JSON.stringify(serverImages.map(p => p.serverFilename)));
      submitData.append('draftImageDescriptions', JSON.stringify(serverImages.map(p => p.description || "")));
    }

    // Image descriptions for newly uploaded images only
    const descriptions = localImages.map(img => img.description || "");
    submitData.append('imageDescriptions', JSON.stringify(descriptions));

    // Actual new image files
    localImages.forEach(imageItem => {
      submitData.append('images', imageItem.file, imageItem.file.name);
    });

    // Original Missionary Report — required; forwarded to the US site and NOT
    // retained on the India side (see routes/submissions.ts).
    if (originalReport) {
      submitData.append('originalReport', originalReport, originalReport.name);
    } else if (savedOriginalReport) {
      submitData.append('draftOriginalReport', savedOriginalReport.filename);
    }

    try {
      const response = await axios.post("/api/submissions/submit", submitData, {
        headers: {
          // Don't set Content-Type for multipart/form-data - let browser set it automatically
        },
      });

      if (response.data.success) {
        setSuccess(`Report submitted successfully! ${response.data.pictureCount || picturesList.length} images uploaded.`);

        // Clean up temporary image previews
        picturesList.forEach(item => {
          if (item.preview && !item.isServerImage) {
            URL.revokeObjectURL(item.preview);
          }
        });

        // Reset form
        setFormData({
          ...formData,
          heardGospel: "",
          receivedJesus: "",
          baptized: "",
          discoveryBibleStudyStarted: "",
          churchesPlanted: "",
          leadersTrained: "",
          testimony: "",
          challenges: "",
          prayerRequests: "",
        });
        setPicturesList([]);
        setCurrentPicture(null);
        setCurrentDescription("");
        document.getElementById("picture-input").value = "";
        setOriginalReport(null);
        setSavedOriginalReport(null);

        // Call onSuccess callback if provided
        if (onSuccess) {
          setTimeout(() => onSuccess(), 1500);
        }
      }
    } catch (error) {
      setError(error.response?.data?.error || "Failed to submit report");
    } finally {
      setLoading(false);
    }
  };

  const months = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  return (
    <div className="missionary-form">
      <h2>
        Monthly Ministry Report for Worker{" "}
        {selectedMissionary?.missionary_number}
      </h2>

      <p style={{ color: 'red', fontWeight: 'bold', margin: '10px 0 15px', fontSize: '14px' }}>
        Please make sure that the information you are entering is for the worker {selectedMissionary?.missionary_number}. Please select the right month and year for your report entry.
      </p>

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="form-section">
          <h3>
            Report Information for Worker{" "}
            {selectedMissionary?.missionary_number}
          </h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="month">Month:</label>
              <select
                id="month"
                name="month"
                value={formData.month}
                onChange={handleChange}
                required
              >
                {months.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="year">Year:</label>
              <input
                type="number"
                id="year"
                name="year"
                value={formData.year}
                onChange={handleChange}
                min="2024"
                max="2030"
                required
              />
            </div>
          </div>
        </div>

        {/* Ministry Statistics */}
        <div className="form-section">
          <h3>Ministry Statistics</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="heardGospel">People Who Heard Gospel:</label>
              <input
                type="number"
                id="heardGospel"
                name="heardGospel"
                value={formData.heardGospel}
                onChange={handleChange}
                min="0"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="receivedJesus">People Who Received Jesus:</label>
              <input
                type="number"
                id="receivedJesus"
                name="receivedJesus"
                value={formData.receivedJesus}
                onChange={handleChange}
                min="0"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="baptized">People Baptized:</label>
              <input
                type="number"
                id="baptized"
                name="baptized"
                value={formData.baptized}
                onChange={handleChange}
                min="0"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="discoveryBibleStudyStarted">
                Discovery Bible Studies Started:
              </label>
              <input
                type="number"
                id="discoveryBibleStudyStarted"
                name="discoveryBibleStudyStarted"
                value={formData.discoveryBibleStudyStarted}
                onChange={handleChange}
                min="0"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="churchesPlanted">Churches Planted:</label>
              <input
                type="number"
                id="churchesPlanted"
                name="churchesPlanted"
                value={formData.churchesPlanted}
                onChange={handleChange}
                min="0"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="leadersTrained">Leaders Trained:</label>
              <input
                type="number"
                id="leadersTrained"
                name="leadersTrained"
                value={formData.leadersTrained}
                onChange={handleChange}
                min="0"
                required
              />
            </div>
          </div>
        </div>

        {/* Narrative Section */}
        <div className="form-section">
          <h3>Ministry Details</h3>
          <div className="form-group">
            <label htmlFor="testimony">Testimony:</label>
            <textarea
              id="testimony"
              name="testimony"
              value={formData.testimony}
              onChange={handleChange}
              rows="4"
              required
              placeholder="Share testimonies from your ministry this month..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="challenges">Challenges:</label>
            <textarea
              id="challenges"
              name="challenges"
              value={formData.challenges}
              onChange={handleChange}
              rows="4"
              required
              placeholder="What challenges did you face this month?"
            />
          </div>

          <div className="form-group">
            <label htmlFor="prayerRequests">Prayer Requests:</label>
            <textarea
              id="prayerRequests"
              name="prayerRequests"
              value={formData.prayerRequests}
              onChange={handleChange}
              rows="4"
              required
              placeholder="How can we pray for you and your ministry?"
            />
          </div>
        </div>

        {/* Pictures Upload - NEW SECTION */}
        <div className="form-section">
          <h3>Pictures (Minimum 2, Maximum 10)</h3>

          {/* Picture Upload Area */}
          <div className="picture-upload-area">
            <div className="form-group">
              <label htmlFor="picture-input">Select Picture:</label>
              <input
                type="file"
                id="picture-input"
                accept="image/*,.heic,.heif"
                onChange={handlePictureSelect}
                disabled={picturesList.length >= 10 || processingImage}
              />
              {processingImage && (
                <div className="processing-message">
                  Processing HEIC image, please wait...
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="picture-description">Picture Description:</label>
              <input
                type="text"
                id="picture-description"
                value={currentDescription}
                onChange={(e) => setCurrentDescription(e.target.value)}
                placeholder="Describe what this picture shows..."
                disabled={!currentPicture || picturesList.length >= 10 || processingImage}
              />
            </div>

            <button
              type="button"
              onClick={handleAddPicture}
              className="add-picture-btn"
              disabled={
                !currentPicture ||
                !currentDescription ||
                picturesList.length >= 10 ||
                processingImage
              }
            >
              {processingImage ? "Processing..." : "Add Picture"}
            </button>

            {picturesList.length >= 10 && (
              <div className="info-message">Maximum 10 pictures reached</div>
            )}
          </div>

          {/* Pictures List */}
          <div className="pictures-list">
            <h4>Added Pictures ({picturesList.length}/10):</h4>
            {picturesList.length === 0 ? (
              <p className="no-pictures">
                No pictures added yet. Please add at least 2 pictures.
              </p>
            ) : (
              <div className="pictures-grid">
                {picturesList.map((item, index) => (
                  <div key={index} className="picture-item">
                    <div className="picture-preview">
                      <img src={item.preview} alt={`Preview ${index + 1}`} />
                    </div>
                    <div className="picture-info">
                      <p className="picture-name">
                        {item.originalName}
                        {item.isConverted && <span className="converted-badge"> (converted from HEIC)</span>}
                        {item.isServerImage && <span style={{ color: '#667eea', fontSize: '11px' }}> (saved)</span>}
                      </p>
                      <p className="picture-description">{item.description}</p>
                      <div className="picture-controls">
                        <button
                          type="button"
                          onClick={() => handleMoveUp(index)}
                          className="move-btn"
                          disabled={index === 0}
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveDown(index)}
                          className="move-btn"
                          disabled={index === picturesList.length - 1}
                          title="Move down"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemovePicture(index)}
                          className="remove-picture-btn"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Original Missionary Report - required, forwarded to the US site */}
        <div className="form-section">
          <h3>Original Missionary Report (Required)</h3>
          <p style={{ margin: '0 0 10px', fontSize: '14px', color: '#555' }}>
            Attach a scan or photo of the original report you worked from. Accepted
            formats: PDF, Word (.doc/.docx) or JPG. Maximum {ORIGINAL_REPORT_MAX_MB} MB.
          </p>
          <div className="form-group">
            <label htmlFor="original-report-input">Select File:</label>
            <input
              type="file"
              id="original-report-input"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg"
              onChange={handleOriginalReportSelect}
              disabled={loading || saving}
            />
          </div>
          {originalReport ? (
            <div style={{ fontSize: '14px', color: '#2f855a', fontWeight: 'bold' }}>
              Attached: {originalReport.name} ({Math.max(1, Math.round(originalReport.size / 1024))} KB)
            </div>
          ) : savedOriginalReport ? (
            <div style={{ fontSize: '14px', color: '#2f855a', fontWeight: 'bold' }}>
              Attached: {savedOriginalReport.name} (saved with draft)
            </div>
          ) : (
            <div style={{ fontSize: '14px', color: '#c53030' }}>
              No file attached yet — this is required before you can submit.
            </div>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <p style={{ color: 'red', fontWeight: 'bold', margin: '15px 0 10px', fontSize: '14px' }}>
          Before submitting, please verify all entered data to be accurate for this worker.
        </p>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
          <button
            type="button"
            onClick={() => handleSaveDraft(false)}
            disabled={saving || loading}
            style={{
              padding: '10px 20px',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: saving || loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: saving || loading ? 0.6 : 1,
            }}
          >
            {saving ? "Saving..." : "Save Draft"}
          </button>
          <button
            type="button"
            onClick={() => handleSaveDraft(true)}
            disabled={saving || loading}
            style={{
              padding: '10px 20px',
              backgroundColor: '#ed8936',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: saving || loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: saving || loading ? 0.6 : 1,
            }}
          >
            {saving ? "Saving..." : "Save & Close"}
          </button>
        </div>

        <button type="submit" className="submit-btn" disabled={loading || saving}>
          {loading ? "Submitting..." : "Submit Report"}
        </button>
      </form>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '450px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          }}>
            <h3 style={{ marginBottom: '15px', color: '#2d3748' }}>Confirm Submission</h3>
            <p style={{ marginBottom: '25px', color: '#4a5568', lineHeight: '1.5' }}>
              This action will submit this report and remove it from your site! Ready to Submit?
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => setShowConfirmation(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#718096',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Go Back to Report
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#48bb78',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MissionaryForm;
